/* eslint-disable max-len, import/no-extraneous-dependencies */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import { getStats } from '../../services/adminApi';
import { formatTimeHuman, getLocalDateString } from '../../utils/formatters';
import './admin.css';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

function StatsDashboard() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setIsLoading(true);
        const data = await getStats();
        setStats(data);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, []);

  const formatPlayTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-puzzle-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-puzzle-background">
        <div className="text-center">
          <p className="text-puzzle-danger mb-4">{error}</p>
          <Link to="/admin/daily" className="text-puzzle-blue hover:underline">
            Back to Admin
          </Link>
        </div>
      </div>
    );
  }

  // Prepare pie chart data for user vs guest
  const userGuestData = [
    { name: 'Registered Users', value: stats.userCompletions },
    { name: 'Guests', value: stats.guestCompletions },
  ].filter((d) => d.value > 0);

  // Prepare device data for bar chart
  const deviceData = [
    { name: 'Mobile', count: stats.deviceStats.mobile },
    { name: 'Tablet', count: stats.deviceStats.tablet },
    { name: 'Desktop', count: stats.deviceStats.desktop },
  ].filter((d) => d.count > 0);

  // Get today's stats from dailyStats (use local date for consistency)
  const today = getLocalDateString();
  const todayStats = stats.dailyStats.find((d) => d.date === today) || { starts: 0, completions: 0 };

  return (
    <div className="min-h-screen bg-puzzle-background py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-puzzle-text">Analytics Dashboard</h1>
          <div className="flex gap-4">
            <Link
              to="/admin/daily-puzzles"
              className="text-puzzle-blue hover:underline"
            >
              Daily Puzzles
            </Link>
            <Link
              to="/admin/images"
              className="text-puzzle-blue hover:underline"
            >
              Image Library
            </Link>
            <Link
              to="/admin/feedback"
              className="text-puzzle-blue hover:underline"
            >
              Feedback
            </Link>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <p className="text-sm text-puzzle-text-secondary mb-1">Total Starts</p>
            <p className="text-3xl font-bold text-puzzle-text">{stats.totalStarts}</p>
          </div>
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <p className="text-sm text-puzzle-text-secondary mb-1">Total Completions</p>
            <p className="text-3xl font-bold text-puzzle-text">{stats.totalCompletions}</p>
          </div>
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <p className="text-sm text-puzzle-text-secondary mb-1">Completion Rate</p>
            <p className="text-3xl font-bold text-puzzle-text">
              {stats.completionRate}
              %
            </p>
          </div>
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <p className="text-sm text-puzzle-text-secondary mb-1">Today&apos;s Completions</p>
            <p className="text-3xl font-bold text-puzzle-text">
              {todayStats.completions}
            </p>
            <p className="text-xs text-puzzle-text-secondary">puzzles completed today</p>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Daily Trend Chart */}
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-puzzle-text mb-4">Activity (Last 30 Days)</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
                    }}
                    interval="preserveStartEnd"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip
                    labelFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="starts" stroke="#3B82F6" name="Starts" strokeWidth={2} />
                  <Line type="monotone" dataKey="completions" stroke="#10B981" name="Completions" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* User vs Guest Pie Chart */}
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-puzzle-text mb-4">Completions by User Type</h2>
            <div className="h-64">
              {userGuestData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={userGuestData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {userGuestData.map((entry, index) => (
                        <Cell key={`cell-${entry.name}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-puzzle-text-secondary">
                  No completion data yet
                </div>
              )}
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#3B82F6]" />
                <span className="text-sm text-puzzle-text-secondary">
                  Users:
                  {' '}
                  {stats.userCompletions}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#10B981]" />
                <span className="text-sm text-puzzle-text-secondary">
                  Guests:
                  {' '}
                  {stats.guestCompletions}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Device Distribution */}
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-puzzle-text mb-4">Device Distribution</h2>
            <div className="h-64">
              {deviceData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={deviceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3B82F6" name="Players" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-puzzle-text-secondary">
                  No device data yet
                </div>
              )}
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-puzzle-text mb-4">Performance Metrics</h2>
            <div className="h-64 flex items-center justify-center">
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <p className="text-sm text-puzzle-text-secondary mb-2">Avg. Play Time</p>
                  <p className="text-4xl font-bold text-puzzle-blue">
                    {formatPlayTime(stats.avgPlayTime)}
                  </p>
                  <p className="text-xs text-puzzle-text-secondary mt-1">to complete</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-puzzle-text-secondary mb-2">Avg. Moves</p>
                  <p className="text-4xl font-bold text-puzzle-blue">
                    {stats.avgMoves}
                  </p>
                  <p className="text-xs text-puzzle-text-secondary mt-1">to complete</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Player Tables Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Today's Players */}
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-puzzle-text mb-4">
              Today&apos;s Completions
              <span className="ml-2 text-sm font-normal text-puzzle-text-secondary">
                (
                {stats.todayPlayers?.length || 0}
                {' '}
                users,
                {' '}
                {stats.todayGuestCount || 0}
                {' '}
                guests)
              </span>
            </h2>
            <div className="max-h-64 overflow-y-auto">
              {stats.todayPlayers?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="text-left text-puzzle-text-secondary border-b">
                    <tr>
                      <th className="pb-2">Username</th>
                      <th className="pb-2">Email</th>
                      <th className="pb-2">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.todayPlayers.map((player) => (
                      <tr key={`${player.username}-${player.completedAt}`} className="border-b border-puzzle-border">
                        <td className="py-2 font-medium">{player.username}</td>
                        <td className="py-2 text-puzzle-text-secondary">{player.email}</td>
                        <td className="py-2 text-puzzle-text-secondary">{formatDateTime(player.completedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-puzzle-text-secondary text-center py-8">No completions today yet</p>
              )}
            </div>
          </div>

          {/* This Month's Players */}
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-puzzle-text mb-4">
              This Month&apos;s Active Users
              <span className="ml-2 text-sm font-normal text-puzzle-text-secondary">
                (
                {stats.monthPlayers?.length || 0}
                {' '}
                users)
              </span>
            </h2>
            <div className="max-h-64 overflow-y-auto">
              {stats.monthPlayers?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="text-left text-puzzle-text-secondary border-b">
                    <tr>
                      <th className="pb-2">Username</th>
                      <th className="pb-2">Email</th>
                      <th className="pb-2 text-center">Completions</th>
                      <th className="pb-2">Last Played</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.monthPlayers.map((player) => (
                      <tr key={player.username} className="border-b border-puzzle-border">
                        <td className="py-2 font-medium">{player.username}</td>
                        <td className="py-2 text-puzzle-text-secondary">{player.email}</td>
                        <td className="py-2 text-center font-semibold text-puzzle-blue">{player.completionCount}</td>
                        <td className="py-2 text-puzzle-text-secondary">{formatDate(player.lastPlayed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-puzzle-text-secondary text-center py-8">No activity this month yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Users Section */}
        <div className="mt-6">
          <h2 className="text-xl font-bold text-puzzle-text mb-4">Registered Users</h2>

          {/* User Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-puzzle-surface rounded-lg shadow p-6">
              <p className="text-sm text-puzzle-text-secondary mb-1">Total Users</p>
              <p className="text-3xl font-bold text-puzzle-text">{stats.totalUsers || 0}</p>
            </div>
            <div className="bg-puzzle-surface rounded-lg shadow p-6">
              <p className="text-sm text-puzzle-text-secondary mb-1">New This Week</p>
              <p className="text-3xl font-bold text-puzzle-blue">{stats.newUsersCount || 0}</p>
              <p className="text-xs text-puzzle-text-secondary">last 7 days</p>
            </div>
          </div>

          {/* User Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* New Users (Last 7 Days) */}
            <div className="bg-puzzle-surface rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-puzzle-text mb-4">
                New Users (Last 7 Days)
                <span className="ml-2 text-sm font-normal text-puzzle-text-secondary">
                  (
                  {stats.newUsers?.length || 0}
                  )
                </span>
              </h3>
              <div className="max-h-64 overflow-y-auto">
                {stats.newUsers?.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="text-left text-puzzle-text-secondary border-b">
                      <tr>
                        <th className="pb-2">Username</th>
                        <th className="pb-2">Email</th>
                        <th className="pb-2">Joined</th>
                        <th className="pb-2 text-center">Completions</th>
                        <th className="pb-2 text-center">Streak</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.newUsers.map((user) => (
                        <tr key={user.email} className="border-b border-puzzle-border">
                          <td className="py-2 font-medium">{user.username}</td>
                          <td className="py-2 text-puzzle-text-secondary">{user.email}</td>
                          <td className="py-2 text-puzzle-text-secondary">{formatDate(user.createdAt)}</td>
                          <td className="py-2 text-center">{user.totalCompletions}</td>
                          <td className="py-2 text-center">
                            {user.currentStreak > 0 ? (
                              <span className="inline-flex items-center gap-1 text-orange-500 font-semibold">
                                {user.currentStreak}
                              </span>
                            ) : (
                              <span className="text-puzzle-text-secondary">0</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-puzzle-text-secondary text-center py-8">No new users this week</p>
                )}
              </div>
            </div>

            {/* All Users */}
            <div className="bg-puzzle-surface rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-puzzle-text mb-4">
                All Users (Recent)
                <span className="ml-2 text-sm font-normal text-puzzle-text-secondary">
                  (showing
                  {' '}
                  {stats.recentUsers?.length || 0}
                  )
                </span>
              </h3>
              <div className="max-h-64 overflow-y-auto">
                {stats.recentUsers?.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="text-left text-puzzle-text-secondary border-b">
                      <tr>
                        <th className="pb-2">Username</th>
                        <th className="pb-2">Email</th>
                        <th className="pb-2">Joined</th>
                        <th className="pb-2 text-center">Completions</th>
                        <th className="pb-2 text-center">Streak</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentUsers.map((user) => (
                        <tr key={user.email} className="border-b border-puzzle-border">
                          <td className="py-2 font-medium">{user.username}</td>
                          <td className="py-2 text-puzzle-text-secondary">{user.email}</td>
                          <td className="py-2 text-puzzle-text-secondary">{formatDate(user.createdAt)}</td>
                          <td className="py-2 text-center">{user.totalCompletions}</td>
                          <td className="py-2 text-center">
                            {user.currentStreak > 0 ? (
                              <span className="inline-flex items-center gap-1 text-orange-500 font-semibold">
                                {user.currentStreak}
                              </span>
                            ) : (
                              <span className="text-puzzle-text-secondary">0</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-puzzle-text-secondary text-center py-8">No users yet</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Top Players Leaderboard */}
        <div className="mt-6">
          <div className="bg-puzzle-surface rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-puzzle-text mb-4">Top Players (All-Time)</h2>
            <div className="overflow-x-auto">
              {stats.topPlayers?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="text-left text-puzzle-text-secondary border-b">
                    <tr>
                      <th className="pb-2 w-12">Rank</th>
                      <th className="pb-2">Username</th>
                      <th className="pb-2">Email</th>
                      <th className="pb-2 text-center">Total Completions</th>
                      <th className="pb-2 text-center">Total Time</th>
                      <th className="pb-2 text-center">Total Moves</th>
                      <th className="pb-2 text-center">Avg Moves</th>
                      <th className="pb-2 text-center">Current Streak</th>
                      <th className="pb-2 text-center">Longest Streak</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topPlayers.map((player, index) => (
                      <tr key={player.username} className="border-b border-puzzle-border">
                        <td className="py-3">
                          {index === 0 && <span className="text-xl">🥇</span>}
                          {index === 1 && <span className="text-xl">🥈</span>}
                          {index === 2 && <span className="text-xl">🥉</span>}
                          {index > 2 && <span className="text-puzzle-text-secondary font-medium">{index + 1}</span>}
                        </td>
                        <td className="py-3 font-medium">{player.username}</td>
                        <td className="py-3 text-puzzle-text-secondary">{player.email}</td>
                        <td className="py-3 text-center font-bold text-puzzle-blue">{player.totalCompletions}</td>
                        <td className="py-3 text-center text-puzzle-text-secondary">{formatTimeHuman(player.totalPlayTime)}</td>
                        <td className="py-3 text-center text-puzzle-text-secondary">{player.totalMoves?.toLocaleString() || 0}</td>
                        <td className="py-3 text-center text-puzzle-text-secondary">{player.avgMoves || 0}</td>
                        <td className="py-3 text-center">
                          {player.currentStreak > 0 ? (
                            <span className="inline-flex items-center gap-1 text-orange-500 font-semibold">
                              🔥
                              {player.currentStreak}
                            </span>
                          ) : (
                            <span className="text-puzzle-text-secondary">0</span>
                          )}
                        </td>
                        <td className="py-3 text-center text-puzzle-text-secondary">{player.longestStreak}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-puzzle-text-secondary text-center py-8">No players yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatsDashboard;
